import { useCallback, useEffect, useMemo, useState } from 'react'
import useLocalStorage from '../../../../../../hooks/useLocalStorage'

const PAGINATION_OPTIONS = [10, 20, 50, 100]

/**
 * Calculate optimal page size based on item count
 * Logic: Select smallest threshold that shows all items on one page (up to 100 max)
 */
const calculateOptimalPageSize = (count) => {
    if (count <= 10) return 10
    if (count <= 20) return 20
    if (count <= 50) return 50
    return 100 // For 51-100+ items
}

/**
 * Custom hook for smart pagination that automatically adjusts page size
 * based on the number of items, while allowing manual override that persists.
 * 
 * @param {number} itemCount - Total number of items to paginate
 * @param {string} storageKey - Unique key for localStorage persistence
 * @returns {Object} Pagination configuration object
 */
const useSmartPagination = (itemCount, storageKey) => {
    const [manualPageSize, setManualPageSize] = useLocalStorage(storageKey, null)
    const [pageSize, setPageSize] = useState(10)

    // Auto-adjust pagination when item count changes (unless manually overridden)
    useEffect(() => {
        if (manualPageSize === null && itemCount > 0) {
            const optimalSize = calculateOptimalPageSize(itemCount)
            setPageSize(optimalSize)
        } else if (manualPageSize !== null) {
            setPageSize(manualPageSize)
        }
    }, [itemCount, manualPageSize])

    /**
     * Handle manual page size changes from user interaction
     */
    const handlePageSizeChange = useCallback((newPageSize) => {
        setManualPageSize(newPageSize)
        setPageSize(newPageSize) // Update immediately for instant UI feedback
    }, [setManualPageSize])

    /**
     * Get Ant Design Table pagination configuration
     * Memoized to prevent unnecessary re-renders
     */
    const paginationConfig = useMemo(() => {
        if (itemCount === 0) {
            return false
        }

        return {
            pageSize,
            pageSizeOptions: PAGINATION_OPTIONS.map(String),
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} flagged submissions`,
        }
    }, [itemCount, pageSize])

    return {
        pageSize,
        paginationConfig,
        handlePageSizeChange,
    }
}

export default useSmartPagination
